import React, { useEffect, useMemo, useRef, useState } from "react";
import { answersPopup } from "./popup";

/** Andhra Pradesh (wordpuzzle.php + js/wordpuzzle.js): drag across the grid to find the sweets. */
const WORDS = ["GAVVALU", "KOVALADDU", "ARISELU", "SUNNUNDALU", "CHAKRALU"];
const GRID_SIZE = 12;
const SUBMIT_AFTER = 2;
/** Right, down and down-right, as in the original. */
const DIRECTIONS = [
  [0, 1],
  [1, 0],
  [1, 1],
];

const randomLetter = () => String.fromCharCode(65 + Math.floor(Math.random() * 26));

function buildGrid() {
  for (let attempt = 0; attempt < 50; attempt += 1) {
    const grid = Array.from({ length: GRID_SIZE }, () =>
      Array(GRID_SIZE).fill("")
    );
    const placedAll = WORDS.every((word) => {
      for (let tries = 0; tries < 100; tries += 1) {
        const [dr, dc] = DIRECTIONS[Math.floor(Math.random() * DIRECTIONS.length)];
        const row = Math.floor(Math.random() * GRID_SIZE);
        const col = Math.floor(Math.random() * GRID_SIZE);
        const endRow = row + dr * (word.length - 1);
        const endCol = col + dc * (word.length - 1);
        if (endRow >= GRID_SIZE || endCol >= GRID_SIZE) continue;
        let free = true;
        for (let i = 0; i < word.length; i += 1) {
          if (grid[row + dr * i][col + dc * i] !== "") {
            free = false;
            break;
          }
        }
        if (!free) continue;
        for (let i = 0; i < word.length; i += 1) {
          grid[row + dr * i][col + dc * i] = word[i];
        }
        return true;
      }
      return false;
    });
    if (placedAll) {
      return grid.map((r) => r.map((c) => c || randomLetter()));
    }
  }
  return Array.from({ length: GRID_SIZE }, () =>
    Array.from({ length: GRID_SIZE }, randomLetter)
  );
}

/** The grid saved in the DB (12 rows of 12 letters), or null when there is none / it is malformed. */
function restoreGrid(saved) {
  const rows = saved && saved.grid;
  if (
    !Array.isArray(rows) ||
    rows.length !== GRID_SIZE ||
    !rows.every((r) => typeof r === "string" && r.length === GRID_SIZE)
  ) {
    return null;
  }
  return rows.map((r) => r.split(""));
}

/** Cells from `start` to `end` when they lie on a right / down / down-right line. */
function pathBetween(start, end) {
  const dRow = end.row - start.row;
  const dCol = end.col - start.col;
  if (dRow < 0 || dCol < 0) return [start];
  if (dRow !== 0 && dCol !== 0 && dRow !== dCol) return [start];
  const steps = Math.max(dRow, dCol);
  const stepRow = dRow === 0 ? 0 : 1;
  const stepCol = dCol === 0 ? 0 : 1;
  const cells = [];
  for (let i = 0; i <= steps; i += 1) {
    cells.push({ row: start.row + stepRow * i, col: start.col + stepCol * i });
  }
  return cells;
}

const cellKey = (c) => `${c.row}:${c.col}`;

export default function WordPuzzle({ api }) {
  const { setScore, initialState, saveProgress, setReveal } = api;
  // After a refresh the same grid, the found words and their highlighted cells come back from the DB.
  const grid = useMemo(() => restoreGrid(initialState) || buildGrid(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [found, setFound] = useState(() =>
    Array.isArray(initialState?.found) ? initialState.found.filter((w) => WORDS.includes(w)) : []
  );
  const [foundCells, setFoundCells] = useState(
    () => new Set(Array.isArray(initialState?.cells) ? initialState.cells.map(String) : [])
  );
  const [drag, setDrag] = useState([]);
  const dragRef = useRef({ active: false, start: null, path: [] });

  useEffect(() => {
    setScore(found.length);
  }, [found.length, setScore]);

  // Right-answers popup: every hidden word, with the ones already found ticked.
  const wordRows = (foundList) =>
    WORDS.map((word) => ({
      label: foundList.includes(word) ? "Found" : "Missed",
      answer: word,
      correct: foundList.includes(word),
    }));

  // Skip shows all the words first.
  useEffect(() => {
    setReveal(() =>
      answersPopup({
        title: "The hidden sweets",
        intro: "These are the words to find.",
        items: wordRows(found),
        columns: true,
      })
    );
    return () => setReveal(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setReveal, found]);

  const submitBusy = useRef(false);
  const onSubmit = async () => {
    if (api.saving || submitBusy.current) return;
    submitBusy.current = true;
    // Missed words: show them (with the found ones) before the score is saved.
    if (found.length < WORDS.length) {
      await answersPopup({
        title: "Here are the words you missed",
        intro: `You found ${found.length} of ${WORDS.length}.`,
        items: wordRows(found),
        columns: true,
      });
    }
    submitBusy.current = false;
    api.submit(found.length, found);
  };

  // Save the grid right away (it is random) and again whenever a word is found.
  useEffect(() => {
    saveProgress({
      grid: grid.map((row) => row.join("")),
      found,
      cells: [...foundCells],
    });
  }, [grid, found, foundCells, saveProgress]);

  const cellAt = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const td = el && el.closest ? el.closest("td[data-row]") : null;
    if (!td) return null;
    return { row: Number(td.dataset.row), col: Number(td.dataset.col) };
  };

  const onPointerDown = (e) => {
    const cell = cellAt(e.clientX, e.clientY);
    if (!cell || foundCells.has(cellKey(cell))) return;
    dragRef.current = { active: true, start: cell, path: [cell] };
    setDrag([cell]);
  };

  const onPointerMove = (e) => {
    const st = dragRef.current;
    if (!st.active) return;
    const cell = cellAt(e.clientX, e.clientY);
    if (!cell) return;
    const path = pathBetween(st.start, cell).filter(
      (c) => !foundCells.has(cellKey(c))
    );
    st.path = path;
    setDrag(path);
  };

  const finishDrag = () => {
    const st = dragRef.current;
    if (!st.active) return;
    st.active = false;

    // State updaters run later, after st.path is reset below, so keep our own copy.
    const path = st.path;
    const letters = path.map((c) => grid[c.row][c.col]).join("");
    const reversed = [...letters].reverse().join("");
    const word = WORDS.find((w) => w === letters || w === reversed);

    if (word && !found.includes(word)) {
      setFound((prev) => [...prev, word]);
      setFoundCells((prev) => {
        const next = new Set(prev);
        path.forEach((c) => next.add(cellKey(c)));
        return next;
      });
    }
    st.path = [];
    setDrag([]);
  };

  useEffect(() => {
    window.addEventListener("pointerup", finishDrag);
    window.addEventListener("pointercancel", finishDrag);
    return () => {
      window.removeEventListener("pointerup", finishDrag);
      window.removeEventListener("pointercancel", finishDrag);
    };
  });

  const dragKeys = new Set(drag.map(cellKey));

  return (
    <div className="desi-puzzle-layout">
      <div className="desi-puzzle">
      <p className="desi-puzzle__hint">
        [ Click and drag to highlight {WORDS.length} words below ]
      </p>
      <table
        className="desi-puzzle__grid"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
      >
        <tbody>
          {grid.map((row, r) => (
            <tr key={r}>
              {row.map((letter, c) => {
                const key = `${r}:${c}`;
                const cls = foundCells.has(key)
                  ? "is-found"
                  : dragKeys.has(key)
                    ? "is-highlighted"
                    : "";
                return (
                  <td key={key} data-row={r} data-col={c} className={cls}>
                    {letter}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <div className="desi-found">
        {found.map((word) => (
          <input key={word} type="text" readOnly value={word} aria-label="Found word" />
        ))}
        {found.length >= SUBMIT_AFTER ? (
          <button
            type="button"
            className="desi-submit"
            disabled={api.saving}
            onClick={onSubmit}
          >
            Submit
          </button>
        ) : null}
      </div>
    </div>
  );
}
